import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Modal, notification, Row } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { setIsLoading as setIsLoadingApp } from "../../../../../app.logic";
import { setAppPageHeader } from "../../../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../../../i18n";
import router from "../../../../../router";
import store from "../../../../../store";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../../utils/button";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchive,
    onClickArchiveAll,
    onClickCreate,
    onClickDelete,
    onClickDeleteAll,
    onClickEdit,
    onClickView
} from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { has, have } from "../../../../../utils/menu-context";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionPdf,
    createRowActionReStatus,
    createRowActionView
} from "../../../../../utils/page";
import { flatWhereGroup } from "../../../../../utils/search";
import { required, validateError } from "../../../../../utils/validate";
import { openModalForm as openModalFormConfirmApprove } from "../../release/request/modal/confirm-approve.logic";
import { openModalForm as openModalFormConfirmReceive } from "../../release/request/modal/confirm-receive.logic";
import { callRequestDoc, callRequestStatus, callRequestType } from "../../../storage-location/request/request.section";
import { callRequestDocGenType } from "../../../storage-location/request/requestor/requestor.section";
import { createConditionAction, createConditionForm } from "./request.condition";
import { createColumns, createField } from "./request.section";
import { callRequestAll } from "../../../storage-location/request/request-approve.logic";
import { callAddressType } from "../../../../../components/address/address.logic";
import { createForm as createFormOpenInspection } from "./open-inspection-info/open-inspection-info.logic";

const CIcon = defineAsyncComponent(() => import("../../../../../components/icon/CIcon"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/request";

export const callRequest = ({ form }) => store.dispatch(`${actionPrefix}/request`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReStatus = ({ data }) => store.dispatch(`${actionPrefix}/reStatus`, { data });
export const callReportOpenInspection = ({ data }) => store.dispatch(`${actionPrefix}/reportOpenInspection`, { data });
export const callResultType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "RELEASE_RESULT", value: searchQuery });

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    const companyNameThIndex = customWhere.findIndex(d => d.name === "refOpenInspectionTemp.refUserCompany.companyNameTh");
    if (companyNameThIndex !== -1) {
        const companyNameThWhere = customWhere[companyNameThIndex];
        customWhere.splice(
            companyNameThIndex,
            1,
            {
                openBucket: true,
                name: "refOpenInspectionTemp.refUserCompany.companyNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            },
            {
                logic: "OR",
                closeBucket: true,
                name: "refOpenInspectionTemp.refUserCompany.fullNameTh",
                type: "String",
                operator: "like",
                value: companyNameThWhere.value
            }
        );
    }
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => {
            (state.page = { ...page, where, sort })
        })
        .finally(() => setIsLoading(false));
};

export const validate = ({ props, data, refs }) => {
    const rules = createRules({ props, data, refs });
    return data.useForm
        .validate(Object.keys(rules))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickSave = ({ props, data, refs, formParams, successText }) => {
    const { viewOnly, routerPrefix } = props;
    return validate({ props, data, refs }).then(() => {
        setIsLoadingApp(true);
        return callRequest({ form: { ...data.useForm.modelRef, ...formParams } })
            .finally(() => setIsLoadingApp(false))
            .then(record => {
                if (successText) notification["success"]({ message: successText });
                if (viewOnly) initForm({ props, data, refs });
                else onClickView({ record, routerPrefix });
            });
    });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog, prefix = "refOpenInspectionTemp." }) => {
    if (!forLog) {
        return {
            select: [
                "**",
                "refOpenInspection.id",
                "refOpenInspection.approveCode",
                "refOpenInspection.refUserCompany.*",
                "refOpenInspection.refUserCompany.refPersonType.*",
                `${prefix}**`,
                `${prefix}refUserCompany.**`,
                `${prefix}refUser.**`,
                `${prefix}refDepartment.*`,
                `${prefix}refOpenInspection.refRelease.refInspection.refInformTemp.approveCode`,
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [
                {
                    name: "statuses",
                    asName: "lastStatus.draft",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "draft" }],
                    sort: [{ name: "createDate", type: "DESC" }, { name: "refStatus.seq", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forRequestRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }, { name: "refStatus.seq", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.forApproveRemark",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        {
                            name: "refStatus.key",
                            type: "String",
                            operator: "in",
                            value: ["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment", "approve", "notApprove"]
                        }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }, { name: "refStatus.seq", type: "DESC" }]
                }
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const { menuContext, requestTypeKeys } = props;
    const whereGroup = [];
    const userInfo = store.state.resource.auth.info?.user;
    whereGroup.push([{ name: "createBy", type: "String", operator: "=", value: userInfo.username }]);
    if (has({ menuContext, key: "forReceive" })) {
        whereGroup.push([
            { name: "refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment?.id }
        ]);
    }
    if (has({ menuContext, key: "forApprove" })) {
        whereGroup.push([
            { name: "refToApproveBy.username", type: "String", operator: "=", value: userInfo.username },
        ]);
    }
    if (has({ menuContext, key: "canViewAll" })) {
        whereGroup.push([{ constant: true, name: "1", type: "String", operator: "=", value: "1" }]);
    }
    const where = [
        { name: "refRequestDoc.key", type: "String", operator: "in", value: ["29"] },
        { name: "refRequestType.key", type: "String", operator: "in", value: requestTypeKeys },
    ];
    return [
        ...where,
        ...flatWhereGroup({ where: whereGroup, logic: "OR" })
    ]
};

export const createForm = () => {
    return {
        model: null,
        refModelId: null,
        refModelTempId: null,
        refRequestType: null,
        refRequestDoc: null,
        refStatus: null,
        draftCode: null,
        requestCode: null,
        refDepartment: null,
        isSignCa: null,
        refOpenInspection: null,
        refOpenInspectionTemp: { ...createFormOpenInspection({}) },
        lastStatus: {
            draft: [{ remark: null, statusDatetime: null }],
            forRequestRemark: [{ remark: null, statusDatetime: null }],
            forApproveRemark: [{ remark: null, statusDatetime: null }]
        }
    };
};

export const createRules = ({ key }) => {
    const prefix = key ? `${key}.` : "";
    return {
        [`${prefix}refOpenInspection`]: [required],
        [`${prefix}refOpenInspectionTemp.inspectionDatetime`]: [required],
        [`${prefix}refOpenInspectionTemp.refTransportByWay`]: [required],
        [`${prefix}refOpenInspectionTemp.vehicleNo`]: [required],
        [`${prefix}refOpenInspectionTemp.netWeight`]: [required],
        [`${prefix}refOpenInspectionTemp.refNetWeightUnit`]: [required],
        [`${prefix}refOpenInspectionTemp.qty`]: [required],
        [`${prefix}refOpenInspectionTemp.refQtyUnit`]: [required],
    };
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.refRequestDoc?.value} (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ props, data }) => {
    const { form, forceDefault, requestTypeKeys } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    const promises = [
        callRequestStatus({}),
        callRequestType({}),
        callRequestDoc({}),
        callRequestDocGenType({}),
        callResultType({}),
        callAddressType({}),
    ];
    return Promise.all(promises).then(arr => {
        const userInfo = store.state.resource.auth.info?.user;
        data.statuses = arr[0];
        data.requestTypes = arr[1];
        data.requestDocs = arr[2];
        data.requestDocGenTypes = arr[3];
        data.resultTypes = arr[4];
        data.addressTypes = arr[5];

        if ((!id && !form) || forceDefault) {
            modelRef.isOfficerCreate = !!userInfo.refOfficerLv;
            modelRef.lastStatus.draft[0].statusDatetime = moment().format("YYYY-MM-DD[T]HH:mm:ssZ");
            modelRef.refRequestType = data.requestTypes.find(d => d.key === requestTypeKeys[0]);
            modelRef.refRequestDoc = data.requestDocs.find(d => d.key === "29");
            modelRef.refDocGenType = data.requestDocGenTypes.find(d => d.key === "1");
        }
    });
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix } = props;
    state.columns = createColumns({ props });
    state.manages = [
        createManageSelect({ menuContext }),
        {
            icon: () => <CIcon file="check.svg"></CIcon>,
            text: () => i18n.global.t("button.signApprove"),
            force: true,
            visible: () => has({ menuContext, key: "forApprove" }),
            rowSelection: true,
            buttonProp: { class: "btn-success" },
            onClick: ({ rowSelection }) => {
                openModalFormConfirmApprove({
                    props,
                    onOk: record => {
                        const forms = rowSelection.selectedRows.map(d => {
                            let form = { ...d, refStatus: record.refStatus };
                            return form;
                        });
                        setIsLoading(true);
                        callRequestAll({ forms })
                            .then(() => notification["success"]({ message: i18n.global.t(`views.request.modal.approve.${record.refStatus?.key}`) }))
                            .then(() => onSearch({ props, page: state.page, state }))
                            .finally(() => setIsLoading(false));
                    }
                });
            },
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canApprove({ props, record }) || record.refStatus?.key !== "waitApprove" })
        },
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canArchive({ props, record }) })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) })];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
        createRowActionReStatus({
            menuContext,
            onClick: ({ record }) => {
                Modal.confirm({
                    title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.reStatus") }),
                    autoFocusButton: "cancel",
                    onOk: () => {
                        setIsLoadingApp(true);
                        callReStatus({ data: record })
                            .finally(() => setIsLoadingApp(false))
                            .then(() => onSearch({ props, page: state.page }));
                    }
                });
            },
            visible: ({ record }) => state.conditionAction.canReStatus({ props, record })
        }),
        createRowActionPdf({
            visible: () => false,
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="search.svg"></CIcon> },
            text: () => i18n.global.t("button.viewRequestDocument"),
            callReport: ({ record }) => callReportOpenInspection({ data: { id: record.id } }),
            fileName: "report.pdf"
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive, feeStatements } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canWaitApprove = state.conditionAction.canWaitApprove({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    const canDelete = state.conditionAction.canDelete({ props, record: initialModel });
    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(
            createButtonSave({
                onClick: () => {
                    data.sendReceive = false;
                    const refOpenInspectionTemp = modelRef.refOpenInspectionTemp;
                    let statusRemark = null;
                    if ([undefined, "draft", "reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forRequestRemark[0].remark;
                    } else if (["waitReceive", "waitResponseCustoms", "waitApprove", "waitPayment"].includes(initialModel.refStatus?.key)) {
                        statusRemark = modelRef.lastStatus.forApproveRemark[0].remark;
                    }
                    onClickSave({
                        props,
                        data,
                        refs,
                        state,
                        formParams: {
                            refOpenInspectionTemp: {
                                ...modelRef.refOpenInspectionTemp,
                                refUserCompany: refOpenInspectionTemp?.refUserCompany?.id ? refOpenInspectionTemp?.refUserCompany : null,
                                refUser: refOpenInspectionTemp?.refUser?.id ? refOpenInspectionTemp?.refUser : null
                            },
                            statusRemark
                        }
                    });
                },
                classStyle: "m-1"
            })
        );
    }
    if (id && isActive && canWaitApprove) {
        const statusWaitApprove = data.statuses.find(status => status.key === "waitApprove");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.sendRequest")}`,
                classStyle: `m-1 ${statusWaitApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmReceive({
                            props: { roleName: "RELEASE_APPROVE"},
                            onOk: record => {
                                const isStatusBack = ["reEditOfficer", "reEditAuthority", "reEditCustoms"].includes(record.refStatus?.key);
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    state,
                                    formParams: {
                                        statusRemark: isStatusBack
                                            ? modelRef.lastStatus.forRequestRemark[0].remark
                                            : modelRef.lastStatus.forApproveRemark[0].remark,
                                        statusRemarkBefore: isStatusBack ? modelRef.lastStatus.forApproveRemark[0].remark : null,
                                        refStatus: record.refStatus,
                                        refToApproveBy: record.refToApproveBy
                                    },
                                    successText: i18n.global.t(`views.request.modal.receive.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove) {
        const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${i18n.global.t("button.signApprove")}`,
                classStyle: `m-1 ${statusApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    validate({ props, data, refs }).then(() => {
                        openModalFormConfirmApprove({
                            props,
                            onOk: record => {
                                const isStatusBack = ["reEditAuthority"].includes(record.refStatus?.key);
                                let formParams = {
                                    statusRemark: isStatusBack
                                        ? modelRef.lastStatus.forRequestRemark[0].remark
                                        : modelRef.lastStatus.forApproveRemark[0].remark,
                                    statusRemarkBefore: isStatusBack
                                        ? modelRef.lastStatus.forApproveRemark?.[0]?.remark
                                        : null,
                                    refStatus: record.refStatus,
                                };
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams,
                                    successText: i18n.global.t(`views.request.modal.approve.${record.refStatus?.key}`)
                                });
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && canDelete) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    if (
        id &&
        feeStatements?.[0]?.id &&
        ((has({ menuContext, key: "forRequest" }) && ![undefined, "draft"].includes(feeStatements?.[0]?.refStatus?.key)) ||
            have({ menuContext, keys: ["forReceive", "forApprove"] }))
    ) {
        col2.push(
            createButton({
                text: `${i18n.global.t("model.feeStatement.modelName")}${feeStatements?.[0] ? ` (${feeStatements[0].refStatus?.value})` : ""}`,
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    validate({ props, data, refs }).then(() => {
                        router.push({ name: `fees-view`, params: { id: feeStatements?.[0]?.id } });
                    });
                },
                icon: <CIcon file="active.svg"></CIcon>
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const state = reactive({
    page: createPage({
        options: {
            select: [
                "*",
                "refDepartment.*",
                "refOpenInspection.*",
                "refOpenInspectionTemp.**",
                "refOpenInspectionTemp.refUserCompany.*",
                "refOpenInspectionTemp.refUserCompany.refPersonType.*",
                "refOpenInspectionTemp.refRelease.refInspection.refRequestDoc.*",
                "refOpenInspectionTemp.refRelease.refInspection.refInformTemp.approveCode",
                "refRequestType.*",
                "refStatus.*",
            ],
            sort: [{ name: "updateDate", type: "DESC" }],
            children: [
                {
                    name: "refOpenInspectionTemp.refRelease.products",
                    select: ["*", "refInspectionProduct.refInformProduct.*"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }],
                },
            ]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
